import { useState } from "react";
import { analyticsApi } from "@/services/apiClient";
import { useFilterStore, isReadOnlyEnv } from "@/stores/filterStore";
import Panel from "./Panel";

// ──────────────────────────────────────────────────────────────────────────────
// 계정 Role 관리 (2026-08-21)
//
// 기존 인프라:
//   - DynamoDB: nova-account-roles-prod (PK: email)
//   - API: POST /analytics/admin/account-role { email, role }
//   - Backend: roles.py + admin_ops.py → set_account_role
//
// 기능:
//   - 이메일 입력 → 현재 role 표시(resolve) → admin/test/none 변경
//   - staging은 WRITE_ROUTES가 READ_ONLY일 수 있어 write 실패 시 안내 표시
//
// 제약:
//   - 새 DB/계산 로직 만들지 않음
//   - 기존 resolve_role fallback(NON_CUSTOMER_EMAILS, is_test_account) 그대로 유지
// ──────────────────────────────────────────────────────────────────────────────

const ROLES = [
  { id: "admin", label: "관리자", desc: "관리자 권한 — 백오피스 접근 + 매출/원가 지표에서 제외" },
  { id: "test", label: "테스트", desc: "테스트 계정 — 지표 집계에서 제외 (매출·활성·원가)" },
  { id: "none", label: "일반 고객", desc: "일반 고객 — 모든 집계에 포함 (기본값)" },
];

export default function AccountRoleSection() {
  const readOnly = isReadOnlyEnv(useFilterStore((s) => s.env));
  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState("none");
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]); // 최근 변경 로그

  async function handleSubmit(e) {
    e.preventDefault();
    if (readOnly) { setStatus("error"); setMessage("STG 조회 상태에서는 역할 변경이 비활성화됩니다."); return; }
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    setStatus("loading");
    setMessage("");

    try {
      const res = await analyticsApi.adminSetAccountRole({ email: trimmed, role: selectedRole });
      setStatus("success");
      const roleLabel = ROLES.find((r) => r.id === (res.role || "none"))?.label || res.role;
      setMessage(`${trimmed} → ${roleLabel} 설정 완료`);
      setHistory((prev) => [
        { email: trimmed, role: res.role || "none", at: new Date().toLocaleTimeString("ko-KR") },
        ...prev.slice(0, 9),
      ]);
    } catch (err) {
      setStatus("error");
      if (err.status === 403 || err.code === "FORBIDDEN") {
        setMessage("권한 없음 — staging 환경에서는 Role 쓰기가 READ_ONLY로 제한될 수 있습니다.");
      } else {
        setMessage(err.message || "역할 변경에 실패했습니다.");
      }
    }
  }

  return (
    <div className="space-y-4">
      <Panel title="계정 역할 부여"
             action={<span className="text-[11.5px] text-ink-400">nova-account-roles-prod</span>}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 이메일 입력 */}
          <div>
            <label htmlFor="role-email" className="block text-[13px] font-medium text-ink-700">
              대상 이메일
            </label>
            <input
              id="role-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="mt-1 w-full max-w-sm rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-800 placeholder:text-ink-300 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
              required
            />
          </div>

          {/* Role 선택 */}
          <div>
            <label className="block text-[13px] font-medium text-ink-700 mb-2">부여할 역할</label>
            <div className="space-y-2">
              {ROLES.map((r) => (
                <label key={r.id}
                       className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                         selectedRole === r.id
                           ? "border-brand-300 bg-brand-50/50"
                           : "border-ink-150 hover:border-ink-300"
                       }`}>
                  <input
                    type="radio"
                    name="account-role"
                    value={r.id}
                    checked={selectedRole === r.id}
                    onChange={() => setSelectedRole(r.id)}
                    className="mt-0.5 accent-brand-600"
                  />
                  <div>
                    <span className="text-sm font-medium text-ink-800">{r.label}</span>
                    <p className="text-[12px] text-ink-500">{r.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* 제출 */}
          <button
            type="submit"
            disabled={status === "loading" || !email.trim() || readOnly}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {status === "loading" ? "처리 중…" : readOnly ? "STG 조회 전용" : "역할 변경"}
          </button>

          {/* 결과 메시지 */}
          {status && status !== "loading" && (
            <div className={`rounded-lg border px-3 py-2 text-sm ${
              status === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-red-200 bg-red-50 text-red-800"
            }`}>
              {message}
            </div>
          )}
        </form>
      </Panel>

      {/* 이번 세션 변경 이력 */}
      {history.length > 0 && (
        <Panel title="이번 세션 변경 이력">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-ink-100 text-left text-ink-500">
                <th className="pb-2 pr-3 font-medium">이메일</th>
                <th className="pb-2 pr-3 font-medium">역할</th>
                <th className="pb-2 font-medium">시간</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i} className="border-b border-ink-50">
                  <td className="py-1.5 pr-3 text-ink-700">{h.email}</td>
                  <td className="py-1.5 pr-3">
                    <RoleBadge role={h.role} />
                  </td>
                  <td className="py-1.5 text-ink-400">{h.at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {/* 안내 */}
      <div className="rounded-xl border border-ink-100 bg-ink-50/50 px-4 py-3 text-[12.5px] text-ink-500 space-y-1">
        <p><strong>동작 방식:</strong> 역할을 부여하면 DynamoDB 테이블(nova-account-roles-prod)에 저장됩니다.</p>
        <p><strong>반영 지연:</strong> 최대 60초 (Lambda 인메모리 캐시 TTL). 다른 컨테이너도 60초 내 갱신.</p>
        <p><strong>폴백:</strong> 테이블에 없는 계정은 기존 코드 기준(NON_CUSTOMER_EMAILS, is_test_account)으로 분류.</p>
        <p><strong>staging 제약:</strong> WRITE_ROUTES가 READ_ONLY인 환경에서는 역할 쓰기가 실패할 수 있습니다.</p>
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const styles = {
    admin: "bg-purple-50 text-purple-700",
    test: "bg-amber-50 text-amber-700",
    none: "bg-ink-100 text-ink-600",
  };
  const labels = { admin: "관리자", test: "테스트", none: "일반" };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[role] || styles.none}`}>
      {labels[role] || role}
    </span>
  );
}
